/**
 * Demo Social Insights Page
 * 
 * A fully self-contained demo page that looks identical to the real Social Insights page
 * but uses hardcoded demo data instead of making API calls.
 * 
 * This page is ONLY shown to the demo tenant (Quantum Leap Labs).
 */
import { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { 
  Linkedin, Users, Eye, ThumbsUp, MessageSquare, Share2, 
  TrendingUp, TrendingDown, BarChart3, DollarSign, Target
} from "lucide-react";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, LineChart, Line, Legend
} from "recharts";
import DateFilter from "@/components/DateFilterDropdown";

// Generate 90 days of demo LinkedIn data
const generateDemoData = () => {
  const data = [];
  const now = new Date();
  let followers = 6200; // Starting followers 90 days ago
  
  for (let i = 89; i >= 0; i--) {
    const date = new Date(now);
    date.setDate(date.getDate() - i);
    
    // Organic growth (2-15 per day)
    const organicGain = Math.floor(Math.random() * 14) + 2;
    // Paid growth (0-8 per day, more likely on weekdays)
    const dayOfWeek = date.getDay();
    const paidGain = dayOfWeek > 0 && dayOfWeek < 6 ? Math.floor(Math.random() * 9) : Math.floor(Math.random() * 3);
    
    followers += organicGain + paidGain;
    
    const impressions = Math.floor(3000 + Math.random() * 7000);
    const engagementRate = 0.02 + Math.random() * 0.04;
    
    data.push({
      date: date.toISOString().split('T')[0],
      followers,
      organicGain,
      paidGain,
      impressions,
      clicks: Math.floor(impressions * (0.01 + Math.random() * 0.02)),
      likes: Math.floor(impressions * engagementRate * 0.6),
      comments: Math.floor(impressions * engagementRate * 0.15),
      shares: Math.floor(impressions * engagementRate * 0.25),
      pageViews: Math.floor(200 + Math.random() * 600)
    });
  }
  
  return data;
};

// Generate demo LinkedIn Ads data
const LINKEDIN_ADS = [
  { campaign: "Thought Leadership", impressions: 125000, clicks: 1875, spend: 4520, leads: 42, ctr: 1.5 },
  { campaign: "Product Demo", impressions: 98000, clicks: 2156, spend: 5890, leads: 67, ctr: 2.2 },
  { campaign: "Case Studies", impressions: 72000, clicks: 1080, spend: 3240, leads: 28, ctr: 1.5 },
  { campaign: "Webinar Promo", impressions: 54000, clicks: 1350, spend: 2970, leads: 85, ctr: 2.5 },
  { campaign: "Brand Awareness", impressions: 186000, clicks: 1674, spend: 4185, leads: 15, ctr: 0.9 }
];

const RECENT_POSTS = [
  { title: "5 Ways AI is Transforming B2B Marketing", likes: 342, comments: 28, shares: 56, impressions: 12400 },
  { title: "Q4 Industry Report: Key Trends for 2026", likes: 289, comments: 45, shares: 78, impressions: 15200 },
  { title: "Customer Success Story: TechCorp", likes: 178, comments: 15, shares: 32, impressions: 8900 },
  { title: "Announcing Our New Analytics Dashboard", likes: 456, comments: 62, shares: 94, impressions: 18700 },
  { title: "The Future of Marketing Automation", likes: 234, comments: 31, shares: 45, impressions: 10300 }
];

export default function DemoSocialInsightsPage() {
  const [dateRange, setDateRange] = useState({ start: null, end: null });
  const [selectedPeriod, setSelectedPeriod] = useState(30);
  
  const allData = useMemo(() => generateDemoData(), []);
  
  const filteredData = useMemo(() => {
    return allData.slice(-selectedPeriod);
  }, [allData, selectedPeriod]);
  
  const totals = useMemo(() => {
    const data = filteredData;
    return {
      currentFollowers: data[data.length - 1]?.followers || 0,
      followerGains: data.reduce((sum, d) => sum + d.organicGain + d.paidGain, 0),
      organicGains: data.reduce((sum, d) => sum + d.organicGain, 0),
      paidGains: data.reduce((sum, d) => sum + d.paidGain, 0),
      impressions: data.reduce((sum, d) => sum + d.impressions, 0),
      clicks: data.reduce((sum, d) => sum + d.clicks, 0),
      likes: data.reduce((sum, d) => sum + d.likes, 0),
      comments: data.reduce((sum, d) => sum + d.comments, 0),
      shares: data.reduce((sum, d) => sum + d.shares, 0),
      pageViews: data.reduce((sum, d) => sum + d.pageViews, 0)
    };
  }, [filteredData]);
  
  const engagementRate = useMemo(() => {
    const totalEngagement = totals.likes + totals.comments + totals.shares;
    return totals.impressions > 0 ? ((totalEngagement / totals.impressions) * 100).toFixed(2) : 0;
  }, [totals]);

  const adsTotals = useMemo(() => ({
    impressions: LINKEDIN_ADS.reduce((sum, a) => sum + a.impressions, 0),
    clicks: LINKEDIN_ADS.reduce((sum, a) => sum + a.clicks, 0),
    spend: LINKEDIN_ADS.reduce((sum, a) => sum + a.spend, 0),
    leads: LINKEDIN_ADS.reduce((sum, a) => sum + a.leads, 0)
  }), []);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="demo-social-insights-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold text-white">Social Insights</h1>
          <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">
            DEMO DATA
          </Badge>
          <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">
            <Linkedin className="w-3 h-3 mr-1" />
            Connected
          </Badge>
        </div>
        <div className="flex items-center gap-2">
          {[7, 30, 90].map((days) => (
            <button
              key={days}
              onClick={() => setSelectedPeriod(days)}
              className={`px-3 py-1 rounded-lg text-sm transition-colors ${
                selectedPeriod === days 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
              }`}
            >
              {days}D
            </button>
          ))}
        </div>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Followers</p>
                <p className="text-2xl font-bold text-white">{totals.currentFollowers.toLocaleString()}</p>
              </div>
              <Users className="w-8 h-8 text-blue-400" />
            </div>
            <div className="flex items-center gap-1 mt-2 text-green-400 text-sm">
              <TrendingUp className="w-3 h-3" />
              <span>+{totals.followerGains} this period</span>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Impressions</p>
                <p className="text-2xl font-bold text-white">{(totals.impressions / 1000).toFixed(1)}K</p>
              </div>
              <Eye className="w-8 h-8 text-purple-400" />
            </div>
            <div className="flex items-center gap-1 mt-2 text-green-400 text-sm">
              <TrendingUp className="w-3 h-3" />
              <span>+18.4%</span>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Engagement</p>
                <p className="text-2xl font-bold text-white">{engagementRate}%</p>
              </div>
              <ThumbsUp className="w-8 h-8 text-green-400" />
            </div>
            <div className="flex items-center gap-1 mt-2 text-green-400 text-sm">
              <TrendingUp className="w-3 h-3" />
              <span>+0.3%</span>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Likes</p>
                <p className="text-2xl font-bold text-white">{totals.likes.toLocaleString()}</p>
              </div>
              <ThumbsUp className="w-8 h-8 text-red-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Comments</p>
                <p className="text-2xl font-bold text-white">{totals.comments.toLocaleString()}</p>
              </div>
              <MessageSquare className="w-8 h-8 text-yellow-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Shares</p>
                <p className="text-2xl font-bold text-white">{totals.shares.toLocaleString()}</p>
              </div>
              <Share2 className="w-8 h-8 text-cyan-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Follower Growth Chart */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-blue-400" />
            Follower Growth
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={filteredData}>
              <defs>
                <linearGradient id="followerGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
              <XAxis 
                dataKey="date" 
                stroke="#64748b"
                tickFormatter={(d) => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              />
              <YAxis stroke="#64748b" domain={['dataMin - 100', 'dataMax + 100']} />
              <Tooltip 
                contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px' }}
                labelStyle={{ color: '#f1f5f9' }}
                formatter={(value, name) => [value.toLocaleString(), name]}
              />
              <Area 
                type="monotone" 
                dataKey="followers" 
                stroke="#3b82f6" 
                fill="url(#followerGradient)" 
                strokeWidth={2}
                name="Total Followers"
              />
            </AreaChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Organic vs Paid & Engagement */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Organic vs Paid Followers */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Users className="w-5 h-5 text-green-400" />
              Organic vs Paid Growth
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/30">
                <p className="text-green-400 text-sm">Organic</p>
                <p className="text-2xl font-bold text-white">+{totals.organicGains}</p>
              </div>
              <div className="p-4 rounded-lg bg-purple-500/10 border border-purple-500/30">
                <p className="text-purple-400 text-sm">Paid</p>
                <p className="text-2xl font-bold text-white">+{totals.paidGains}</p>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={filteredData.slice(-14)}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis 
                  dataKey="date" 
                  stroke="#64748b"
                  tickFormatter={(d) => new Date(d).toLocaleDateString('en-US', { day: 'numeric' })}
                />
                <YAxis stroke="#64748b" />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px' }}
                />
                <Bar dataKey="organicGain" stackId="a" fill="#22c55e" name="Organic" />
                <Bar dataKey="paidGain" stackId="a" fill="#a855f7" name="Paid" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Daily Impressions */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Eye className="w-5 h-5 text-purple-400" />
              Daily Impressions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={268}>
              <LineChart data={filteredData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis 
                  dataKey="date" 
                  stroke="#64748b"
                  tickFormatter={(d) => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                />
                <YAxis stroke="#64748b" />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px' }}
                  formatter={(value) => [value.toLocaleString(), 'Impressions']}
                />
                <Line 
                  type="monotone" 
                  dataKey="impressions" 
                  stroke="#a855f7" 
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* LinkedIn Ads Section */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-green-400" />
            LinkedIn Ads Performance
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Ads Totals */}
          <div className="grid grid-cols-4 gap-4 mb-6">
            <div className="p-4 rounded-lg bg-slate-700/50">
              <p className="text-slate-400 text-sm">Total Spend</p>
              <p className="text-xl font-bold text-white">${adsTotals.spend.toLocaleString()}</p>
            </div>
            <div className="p-4 rounded-lg bg-slate-700/50">
              <p className="text-slate-400 text-sm">Impressions</p>
              <p className="text-xl font-bold text-white">{(adsTotals.impressions / 1000).toFixed(0)}K</p>
            </div>
            <div className="p-4 rounded-lg bg-slate-700/50">
              <p className="text-slate-400 text-sm">Clicks</p>
              <p className="text-xl font-bold text-white">{adsTotals.clicks.toLocaleString()}</p>
            </div>
            <div className="p-4 rounded-lg bg-slate-700/50">
              <p className="text-slate-400 text-sm">Leads</p>
              <p className="text-xl font-bold text-white">{adsTotals.leads}</p>
            </div>
          </div>

          {/* Campaign Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-700">
                  <th className="text-left text-slate-400 text-sm font-medium py-3">Campaign</th>
                  <th className="text-right text-slate-400 text-sm font-medium py-3">Impressions</th>
                  <th className="text-right text-slate-400 text-sm font-medium py-3">Clicks</th>
                  <th className="text-right text-slate-400 text-sm font-medium py-3">CTR</th>
                  <th className="text-right text-slate-400 text-sm font-medium py-3">Spend</th>
                  <th className="text-right text-slate-400 text-sm font-medium py-3">Leads</th>
                  <th className="text-right text-slate-400 text-sm font-medium py-3">CPL</th>
                </tr>
              </thead>
              <tbody>
                {LINKEDIN_ADS.map((ad) => (
                  <tr key={ad.campaign} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                    <td className="py-3 text-white">{ad.campaign}</td>
                    <td className="py-3 text-slate-300 text-right">{ad.impressions.toLocaleString()}</td>
                    <td className="py-3 text-slate-300 text-right">{ad.clicks.toLocaleString()}</td>
                    <td className="py-3 text-slate-300 text-right">{ad.ctr}%</td>
                    <td className="py-3 text-slate-300 text-right">${ad.spend.toLocaleString()}</td>
                    <td className="py-3 text-slate-300 text-right">{ad.leads}</td>
                    <td className="py-3 text-slate-300 text-right">${(ad.spend / ad.leads).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Recent Posts */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-blue-400" />
            Top Performing Posts
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {RECENT_POSTS.map((post, idx) => (
              <div key={idx} className="p-4 rounded-lg bg-slate-700/30 hover:bg-slate-700/50 transition-colors">
                <p className="text-white font-medium mb-2">{post.title}</p>
                <div className="flex items-center gap-6 text-sm text-slate-400">
                  <span className="flex items-center gap-1">
                    <Eye className="w-4 h-4" />
                    {post.impressions.toLocaleString()}
                  </span>
                  <span className="flex items-center gap-1">
                    <ThumbsUp className="w-4 h-4" />
                    {post.likes}
                  </span>
                  <span className="flex items-center gap-1">
                    <MessageSquare className="w-4 h-4" />
                    {post.comments}
                  </span>
                  <span className="flex items-center gap-1">
                    <Share2 className="w-4 h-4" />
                    {post.shares}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
